import { Injectable } from '@angular/core';
import { UrlTree, Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { AuthService, UserType } from 'src/app/domain/auth/service/auth.service';
import { NotificationsService } from '../../core/service/notifications.service';

type GuardReturnType = Promise<boolean | UrlTree>;

@Injectable()
export class AuthGuard {

  constructor(
    private cookie: CookieService,
    private router: Router,
    private auth: AuthService,
    private notificationService: NotificationsService,
  ) {
  }

  async canActivate(): GuardReturnType {
    const authCookie = this.cookie.get('user_info');
    const isTokenAvailable = this.checkTokens(authCookie);
    if (!isTokenAvailable) {
      await this.router.navigateByUrl('/login');
      return false;
    }
    await this.checkExpirationDate(authCookie);
    this.checkUserType();
    this.auth.changeIsLoggedIn(true);
    return true;
  }

  private getLoggedInUserType(): UserType {
    const isAdmin = window.location.pathname.includes('admin');
    return isAdmin ? 'admin' : 'viewer';
  }

  private async checkExpirationDate(authCookie: string): Promise<void> {
    const splitAuthCookie = authCookie.split('&');
    const expDate = Number(splitAuthCookie[3]);
    const currentDate = Math.trunc((new Date()).getTime() / 1000);
    if (expDate < currentDate) {
      this.notificationService.warning({
        title: 'Cookies expired',
        message: `Auth cookie is ${currentDate - expDate} seconds expired.`,
      });
      await this.router.navigateByUrl('/login');
    }
  }

  private checkTokens(authCookie: string): boolean {
    if (authCookie) {
      return true;
    }
    this.notificationService.warning({ title: 'Cookies not found', message: 'Auth token is not provided.' });
    return false;
  }

  /**
   * Checks the current user type and sets it if it's undefined.
   * If the current user type is undefined, this method tries to retrieve it from local storage.
   * If it is not found in local storage, the user type is set to the value returned by `getLoggedInUserType()`.
   *
   * NOTE: `localStorage.getItem()` returns the string `'undefined'` if the userType is not found in local storage.
   * This method includes an extra check to handle that behaviour.
   */
  // save admin or viewer as current user type after page reload.
  private checkUserType(): void {
    const userType = this.auth.getCurrentUserType();
    if (!userType) {
      const userTypeFromLocalStorageString = localStorage.getItem('userType');
      this.auth.setCurrentUserType(userTypeFromLocalStorageString !==
      'undefined' ? userTypeFromLocalStorageString as UserType : this.getLoggedInUserType());
    }
  }
}
